// src/components/TitleScreen.ts
import { hasSave, loadSave } from '../app/storage.js';

export function TitleScreen(opts: {
  onNewGame: () => void;
  onContinue: () => void;
  onOptions: () => void;
}): HTMLElement {
  const el = document.createElement('div');
  el.className = 'panel';
  const saveExists = hasSave();
  const save = saveExists ? loadSave() : null;
  const playerName = save?.profile.name ?? '—';
  const regionLabel = save?.world.region ?? 'aurora_borealis';
  const updatedAt = save ? new Date(save.meta.updatedAt).toLocaleString('es-ES') : '';

  el.innerHTML = `
    <div class="panel__head">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:.75rem;flex-wrap:wrap">
        <span class="eyebrow">Fase 2 · Exploración jugable</span>
        <span class="chip chip--ok">● Villa Aurelia + Ruta Aurelia</span>
      </div>
      <div class="logo-wrap" style="padding-bottom:.2rem">
        <div class="logo-orb float" aria-hidden="true"><div class="logo-orb__core"></div></div>
      </div>
      <div class="h1" style="text-align:center"><span>AETHERIA</span></div>
      <p class="subtitle" style="text-align:center">Un mundo por descubrir</p>
      <p style="text-align:center;margin-top:.55rem;color:var(--faint);font-family:var(--font-mono);font-size:.68rem;letter-spacing:.12em;text-transform:uppercase">
        Aventura · Exploración · Asterias
      </p>
    </div>
    <div class="panel__body">
      <div class="btn-row" role="group" aria-label="Menú principal">
        <button class="btn btn--primary" data-action="newgame" type="button">
          <span class="btn__icon">✦</span> Nueva partida
        </button>
        <button class="btn btn--ghost" data-action="continue" type="button" ${saveExists ? '' : 'disabled aria-disabled="true" title="Aún no hay partida guardada"'}>
          <span class="btn__icon">↻</span> Continuar ${saveExists ? `· ${playerName}` : ''}
        </button>
        <button class="btn btn--subtle" data-action="options" type="button">
          <span class="btn__icon">⚙</span> Opciones
        </button>
      </div>

      ${
        saveExists
          ? `
      <div class="world-preview" aria-label="Resumen de partida">
        <div class="world-preview__map" aria-hidden="true">
          <div class="world-preview__grid"></div>
          <div class="world-preview__pin" style="left:34%;top:42%"></div>
          <div class="world-preview__pin" style="left:68%;top:58%;opacity:.7;transform:translate(-50%,-50%) scale(.85)"></div>
          <div class="world-preview__pin" style="left:52%;top:28%;opacity:.5;transform:translate(-50%,-50%) scale(.7)"></div>
          <svg viewBox="0 0 400 132" style="position:absolute;inset:auto 0 0 0;height:54px;opacity:.95" preserveAspectRatio="none" aria-hidden="true">
            <path d="M0 52 L60 36 L110 48 L160 22 L210 40 L260 18 L310 38 L360 26 L400 36 L400 132 L0 132 Z" fill="#0a1430" stroke="rgba(255,255,255,.08)" stroke-width="1"/>
            <path d="M0 74 L70 62 L130 78 L190 58 L250 72 L320 60 L400 72" fill="none" stroke="rgba(124,240,255,.22)" stroke-width="1.2" stroke-dasharray="6 6"/>
          </svg>
        </div>
        <div class="world-preview__body">
          <div>
            <div class="world-preview__title">${escapeHtml(playerName)} · ${escapeHtml(regionLabel)}</div>
            <div class="world-preview__sub">${escapeHtml(updatedAt)} · Cap. ${save?.story.chapter ?? 0}</div>
          </div>
          <span class="chip">Slot 1</span>
        </div>
      </div>
      `
          : `
      <div class="notice" style="margin-top:.9rem">
        <strong>¿Primera vez en Aetheria?</strong> Pulsa <em>Nueva partida</em> y elige tu nombre. Tu progreso se guardará localmente y,
        en fases siguientes, se sincronizará con Cloudflare D1.
      </div>
      `
      }

      <div class="stat-grid" aria-label="Estado del mundo">
        <div class="stat"><div class="stat__label">Región</div><div class="stat__value">Villa Aurelia</div></div>
        <div class="stat"><div class="stat__label">Exploración</div><div class="stat__value">Fase 2 ✓</div></div>
        <div class="stat"><div class="stat__label">Versión</div><div class="stat__value">0.2.0</div></div>
      </div>

      <div class="divider"></div>
      <p style="font-size:.76rem;color:var(--faint);line-height:1.6;text-align:center">
        Villa Aurelia + Ruta Aurelia + Interiores + NPCs + Día/Noche.<br/>
        Movimiento fluido · Cámara suave · Colisiones · Diálogos · Guardado.
      </p>
    </div>
  `;

  el.querySelector<HTMLButtonElement>('[data-action="newgame"]')!.addEventListener('click', opts.onNewGame);
  el.querySelector<HTMLButtonElement>('[data-action="continue"]')!.addEventListener('click', () => {
    if (!saveExists) return;
    opts.onContinue();
  });
  el.querySelector<HTMLButtonElement>('[data-action="options"]')!.addEventListener('click', opts.onOptions);

  return el;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
